import { ROUTES } from "@/ai-coding-outputs/navigation";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { MultiSelect, type Option } from "@/components/ui/multi-select";
import { useCustomers } from "@/customers/useCustomers";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useCustomerFilter } from "../useCustomerFilter";
import { useNextUnreviewedEncounter } from "./useNextUnreviewedEncounter";

const HumanReviewWorklistNext = () => {
  const { role } = useAuth();
  const { customers } = useCustomers();
  const { customerIds, setCustomerIds, clearCustomerIds } = useCustomerFilter();
  const [selectedCustomers, setSelectedCustomers] = useState<Option[]>([]);

  // For admin users: use persisted customer IDs, for non-admin users: use all customers
  const effectiveCustomerIds = role === "admin" ? customerIds : customers.map((c) => c.id);

  // Only enable query for admin users if they have selected customers, or for non-admin users
  const isQueryEnabled = role !== "admin" || customerIds.length > 0;
  const hasSearched = isQueryEnabled && effectiveCustomerIds.length > 0;

  const { data, isLoading, error, refetch, isFetching } = useNextUnreviewedEncounter({
    customerIds: effectiveCustomerIds,
    enabled: hasSearched,
  });
  const navigate = useNavigate();

  // Auto-redirect when an encounter is found (only when fetch has completed, so we use fresh data not cache)
  useEffect(() => {
    if (data?.encounterId && !isFetching && (role !== "admin" || customerIds.length > 0)) {
      const url = `${ROUTES.humanCodingReview.route}/${data.encounterId}${data.classificationResultId ? `?classificationResultId=${data.classificationResultId}` : ""}`;
      navigate(url);
    }
  }, [data, isFetching, navigate, role, customerIds]);

  const handleSubmit = () => {
    const newCustomerIds = selectedCustomers.map((c) => c.value);
    setCustomerIds(newCustomerIds);
  };

  const handleReset = () => {
    setSelectedCustomers([]);
    clearCustomerIds();
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (!data) {
    return (
      <div className="space-y-4">
        {role === "admin" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Customers</label>
              <MultiSelect
                options={customers.map((c) => ({ value: c.id, label: c.label }))}
                value={selectedCustomers}
                onChange={setSelectedCustomers}
                placeholder="Select customers..."
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSubmit} disabled={selectedCustomers.length === 0}>
                Search
              </Button>
              {customerIds.length > 0 && (
                <Button onClick={handleReset} variant="outline">
                  Reset
                </Button>
              )}
            </div>
          </div>
        )}
        {hasSearched && (
          <div className="space-y-2">
            <div className="text-lg font-medium">
              🎉 All caught up. There are no more cases to review right now. Check back again tomorrow.
            </div>
            <Button onClick={() => refetch()} variant="outline">
              Refresh
            </Button>
          </div>
        )}
      </div>
    );
  }

  // Create customer options from persisted IDs
  const persistedCustomerOptions = customers
    .filter((c) => customerIds.includes(c.id))
    .map((c) => ({ value: c.id, label: c.label }));

  return (
    <div className="space-y-4">
      {role === "admin" && customerIds.length > 0 && (
        <div className="space-y-2">
          <div className="text-sm text-muted-foreground">
            Showing results for {persistedCustomerOptions.map((c) => c.label).join(", ")}
          </div>
          <Button onClick={handleReset} variant="outline">
            Change Selection
          </Button>
        </div>
      )}
      <div>Next encounter to review: {data.encounterId}</div>
    </div>
  );
};

export default HumanReviewWorklistNext;
